/**
 * @package   EGI / Frontend — wishlist
 * @author    Padmin D. Curtis (AI Partner OS3.0) for Fabio Cherici
 * @version   1.0.0 (M-EGI-373)
 * @date      2026-07-03
 * @purpose   Contratti di tipo dell'isola wishlist (design FRONTEND_WISHLIST.md §2.2):
 *            schema item (riuso 1:1 FABIO-GIANNI wishlist-context), config #wishlist-config
 *            emesso dal Blade (type-guard runtime, pattern carousel-pager isPagerLabels),
 *            eventi DOM di integrazione (§2.3). Zero `any` (tsconfig strict).
 */

export interface WishlistItem {
    id: string;
    title: string;
    imageUrl: string;
    addedAt: number;
}

export interface WishlistEndpoints {
    /** Template POST toggle — placeholder __ID__ (route api.egis.wishlist.toggle). */
    toggle: string;
    /** Template DELETE remove idempotente — placeholder __ID__ (route api.egis.wishlist.remove). */
    remove: string;
    /** GET lista canonica (route api.wishlist.index). */
    list: string;
    /** POST merge-at-boot lista guest (route api.wishlist.merge). */
    merge: string;
}

export interface WishlistLabels {
    added: string;
    removed: string;
    merged: string;
    mergeSkipped: string;
    error: string;
    navLabel: string;
    /** Template con :count (risolto client-side — design §1.2). */
    navCount: string;
    countOne: string;
    /** Template con :count. */
    countOther: string;
    /** Template con :title. */
    removeNamed: string;
}

export interface WishlistConfig {
    authenticated: boolean;
    endpoints: WishlistEndpoints;
    labels: WishlistLabels;
    /**
     * Sezione carrello (M-EGI-385 — ADR-C6: stessa isola). Volutamente
     * `unknown`: la valida il type-guard del modulo cart (cart/types.ts);
     * assente/malformata → solo il modulo cart resta spento.
     */
    cart?: unknown;
}

const ENDPOINT_KEYS: ReadonlyArray<keyof WishlistEndpoints> = ['toggle', 'remove', 'list', 'merge'];
const LABEL_KEYS: ReadonlyArray<keyof WishlistLabels> = [
    'added', 'removed', 'merged', 'mergeSkipped', 'error',
    'navLabel', 'navCount', 'countOne', 'countOther', 'removeNamed',
];

function isStringRecord(value: unknown, keys: ReadonlyArray<string>): boolean {
    if (typeof value !== 'object' || value === null) {
        return false;
    }
    const record = value as Record<string, unknown>; // narrow post-guard: solo lettura chiavi
    return keys.every((key) => typeof record[key] === 'string');
}

/** Type-guard del blocco #wishlist-config: JSON malformato → isola spenta (pattern carousel-pager). */
export function isWishlistConfig(value: unknown): value is WishlistConfig {
    if (typeof value !== 'object' || value === null) {
        return false;
    }
    const record = value as Record<string, unknown>;
    return (
        typeof record.authenticated === 'boolean' &&
        isStringRecord(record.endpoints, ENDPOINT_KEYS) &&
        isStringRecord(record.labels, LABEL_KEYS)
    );
}

// ── Eventi DOM (contratto di integrazione — design §2.3) ─────────────────────

export const CHANGED_EVENT = 'egi:wishlist:changed';
export const MERGED_EVENT = 'egi:wishlist:merged';

export type WishlistChangedAction = 'add' | 'remove' | 'sync';

export interface WishlistChangedDetail {
    count: number;
    id?: string;
    action: WishlistChangedAction;
}

export interface WishlistMergedDetail {
    merged: number;
    skipped: string[];
    count: number;
}

declare global {
    interface Window {
        /** Unico global esposto dall'isola (come window.portfolioEditorModal — design §2.3). */
        egiWishlist?: { refreshToggles: (root?: ParentNode) => void };
    }
}
